import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Menu, X } from 'lucide-react';

const PageHeader = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    const navLinks = [
        { label: 'For Talent', path: '/for-talent' },
        { label: 'For Employers', path: '/for-employers' },
    ];

    // Pre-select the correct registration type based on the current public route.
    const registerPath = location.pathname === '/for-talent'
        ? '/register?type=candidate'
        : location.pathname === '/for-employers'
            ? '/register?type=company'
            : '/register';

    return (
        <header className="sticky top-0 z-50 bg-[#F0FDF4]/80 backdrop-blur-xl border-b border-purple-200/50 shadow-sm" data-testid="page-header">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')} data-testid="header-logo">
                    <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                    <span className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                </div>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center space-x-8" data-testid="header-nav-desktop">
                    {navLinks.map((link) => (
                        <button
                            key={link.path}
                            onClick={() => navigate(link.path)}
                            className={`text-sm font-semibold transition-colors duration-200 ${
                                location.pathname === link.path
                                    ? 'text-[#8B5CF6]'
                                    : 'text-slate-600 hover:text-[#8B5CF6]'
                            }`}
                            style={{ fontFamily: 'Barlow, sans-serif' }}
                            data-testid={`header-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>

                {/* Desktop CTA */}
                <div className="hidden md:flex items-center space-x-4">
                    <Button
                        variant="outline"
                        className="rounded-full border-2 border-purple-500 text-purple-700 hover:bg-purple-100"
                        onClick={() => navigate('/login')}
                        data-testid="header-login-btn"
                    >
                        Log In
                    </Button>
                    <Button
                        className="btn-primary"
                        onClick={() => navigate(registerPath)}
                        data-testid="header-register-btn"
                    >
                        Get Started
                        <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </div>

                {/* Mobile burger */}
                <button
                    className="md:hidden p-2 text-slate-700"
                    onClick={() => setMobileOpen(!mobileOpen)}
                    data-testid="header-mobile-toggle"
                >
                    {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            {/* Mobile menu */}
            {mobileOpen && (
                <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-purple-200/50 px-4 pb-6 pt-2" data-testid="header-mobile-menu">
                    {navLinks.map((link) => (
                        <button
                            key={link.path}
                            onClick={() => { navigate(link.path); setMobileOpen(false); }}
                            className={`block w-full text-left py-3 text-sm font-semibold ${
                                location.pathname === link.path ? 'text-[#8B5CF6]' : 'text-slate-600'
                            }`}
                            data-testid={`header-mobile-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                            {link.label}
                        </button>
                    ))}
                    <div className="flex flex-col gap-3 mt-4">
                        <Button
                            variant="outline"
                            className="rounded-full border-2 border-purple-500 text-purple-700 hover:bg-purple-100 w-full"
                            onClick={() => { navigate('/login'); setMobileOpen(false); }}
                            data-testid="header-mobile-login-btn"
                        >
                            Log In
                        </Button>
                        <Button
                            className="btn-primary w-full"
                            onClick={() => { navigate(registerPath); setMobileOpen(false); }}
                            data-testid="header-mobile-register-btn"
                        >
                            Get Started
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </header>
    );
};

export default PageHeader;
